import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Card, Alert, Button, Input, Select } from '@dreamtree-org/twreact-ui';
import { useAppApi, useStableApi } from '@/lib/api';
import Can from '@rbac/Can';
import DataTable from '@/components/DataTable';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import { isMultiTenant } from '@/lib/tenantAuth';
import { getAuth } from '@rbac/permissions';

/**
 * Multi-company mode: a token acts in one branch (the issuer's), or — issued by a company
 * administrator — company-wide, where each call may pick a branch with X-Branch-Id
 * (models/ApiToken.model.js, docs/PUBLIC-API.md). Applications no longer exist there.
 */
function branchScopeOptions(auth) {
  const active = (auth.branches || []).find((b) => b.id === auth.activeBranchId);
  const name = active ? ' (' + (active.name || active.code) + ')' : '';
  const options = [{ label: `This branch${name}`, value: 'branch' }];
  if (auth.isTenantAdmin) options.push({ label: 'All branches (company-wide — callers choose with X-Branch-Id)', value: 'company' });
  return options;
}

function branchLabel(auth, branchId) {
  if (!branchId) return 'company-wide';
  const b = (auth.branches || []).find((x) => x.id === branchId);
  return b ? (b.name || b.code) : 'another branch';
}

function parseScopes(scopes) {
  try {
    const v = JSON.parse(scopes || '[]');
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

/** revoked wins over expired: a revoked token is dead regardless of its expiry. */
function TokenStatusBadge({ row }) {
  if (row.revoked_at) return <Badge variant="error" size="sm">revoked</Badge>;
  if (row.expires_at && new Date(row.expires_at) < new Date()) {
    return <Badge variant="warning" size="sm">expired</Badge>;
  }
  return <Badge variant="success" size="sm">active</Badge>;
}

/**
 * API Tokens (`/api-token`) — issue/rotate/revoke `tnt_` tokens for the public REST
 * API (/api/v1) and MCP server (/mcp). Tokens are company-bound at issue and the raw
 * value is shown EXACTLY ONCE (models/ApiToken.model.js stores only the hash); the
 * issue dialog's scope picker is the least-privilege gate — grant only what the
 * integration needs.
 */
const SCOPE_CATALOG = [
  { value: 'calls:view', label: 'calls:view — read calls, transcripts, recordings, deliveries' },
  { value: 'calls:dial', label: 'calls:dial — place/schedule/cancel calls & campaigns (spends credits!)' },
  { value: 'calls:configure', label: 'calls:configure — manage webhook endpoints & redelivery' },
  { value: 'agents:view', label: 'agents:view — list agent personas' },
  { value: 'agents:chat', label: 'agents:chat — headless chat with agents' },
  { value: 'knowledge:view', label: 'knowledge:view — semantic knowledge-base search' },
  { value: 'workflows:view', label: 'workflows:view — read workflow definitions, runs and step timelines' },
  { value: 'workflows:run', label: 'workflows:run — trigger and cancel workflow runs (spends provider credits!)' },
  { value: 'tools:run', label: 'tools:run — run server-side tool services, e.g. prescription verification (spends provider credits!)' },
  { value: 'core:view', label: 'core:view — read token metadata & API audit log' },
];

const EXPIRY_OPTIONS = [
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days (recommended)' },
  { value: '365', label: '1 year' },
  { value: 'never', label: 'No expiry (not recommended)' },
];

function TokenReveal({ token, onDismiss }) {
  const curl = `curl -H "Authorization: Bearer ${token}" ${window.location.origin}/api/v1/agents`;
  const mcp = JSON.stringify({ mcpServers: { 'ai-agent-builder': { url: `${window.location.origin}/mcp`, headers: { Authorization: `Bearer ${token}` } } } }, null, 2);
  return (
    <Alert variant="warning" title="API token — copy it now, it will not be shown again">
      <code className="block break-all text-xs bg-[var(--app-surface)] rounded p-2 my-2">{token}</code>
      <details className="text-xs mb-2">
        <summary className="cursor-pointer">Usage examples (REST + MCP)</summary>
        <pre className="bg-[var(--app-surface)] rounded p-2 mt-1 overflow-x-auto">{curl}</pre>
        <pre className="bg-[var(--app-surface)] rounded p-2 mt-1 overflow-x-auto">{mcp}</pre>
      </details>
      <Button size="sm" variant="secondary" onClick={onDismiss}>I saved it</Button>
    </Alert>
  );
}

function IssueDialog({ onClose, onIssued }) {
  const { fetchApi } = useAppApi();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState(['calls:view']);
  const [expiry, setExpiry] = useState('90');
  const [applicationId, setApplicationId] = useState(null);
  const [apps, setApps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const multi = isMultiTenant();
  const auth = getAuth() || {};
  const [branchScope, setBranchScope] = useState('branch');

  useEffect(() => {
    if (multi) return;
    fetchApi('applicationsList').then((res) => setApps((Array.isArray(res) ? res : res?.data) || [])).catch(() => {});
  }, []);

  const submit = async () => {
    setBusy(true);
    setErr('');
    try {
      const expiresAt = expiry === 'never' ? null : new Date(Date.now() + Number(expiry) * 24 * 3600 * 1000).toISOString().slice(0, 19).replace('T', ' ');
      const res = await fetchApi('crud', {
        model: 'ApiToken', action: 'issue',
        data: multi
          ? { name, scopes, expires_at: expiresAt, company_wide: branchScope === 'company' }
          : { name, scopes, expires_at: expiresAt, application_id: applicationId || null },
      });
      onIssued((res.data && res.data.token) || null);
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-3 space-y-3 border-primary-300">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">Issue API token</h2>
        <button className="text-sm text-secondary-500" onClick={onClose}>Close</button>
      </div>
      {err && <Alert variant="error" title="Could not issue token">{err}</Alert>}
      <div className="grid grid-cols-2 gap-3">
        <Input label="Name (what integration uses it)" value={name} onChange={(e) => setName(e.target.value)} />
        <Select label="Expiry" options={EXPIRY_OPTIONS} value={expiry} onChange={(v) => setExpiry(v)} />
      </div>
      {multi ? (
        <div className="grid grid-cols-2 gap-3">
          <Select label="Acts in" options={branchScopeOptions(auth)} value={branchScope} onChange={(v) => setBranchScope(v || 'branch')} />
        </div>
      ) : (
      <div className="grid grid-cols-2 gap-3">
        <Select
          label="Application (scope to one app, or leave empty for company-wide)"
          options={[{ label: 'Company-wide (all apps)', value: '' }, ...apps.map((a) => ({ label: a.name, value: a.id }))]}
          value={applicationId || ''}
          onChange={(v) => setApplicationId(v || null)}
          allowClear
          searchable
        />
      </div>
      )}
      <Select
        label="Scopes (least privilege — grant only what this integration needs)"
        multiSelect
        searchable
        options={SCOPE_CATALOG}
        value={scopes}
        onChange={(v) => setScopes(Array.isArray(v) ? v : [])}
      />
      <div className="text-right">
        <Button onClick={submit} disabled={busy || !name.trim() || !scopes.length}>{busy ? 'Issuing…' : 'Issue token'}</Button>
      </div>
    </Card>
  );
}

/** Workflow bindings named best-effort — a viewer without workflows.view.org still sees
 *  the token rows, just with the numeric binding instead of a name. Mount-only: the
 *  workflow list is fetched once, so the api object must not sit in the deps array. */
function useWorkflowsMap() {
  const { fetchApi } = useStableApi();
  const [workflowsById, setWorkflowsById] = useState(new Map());
  useEffect(() => {
    fetchApi('crud', { model: 'AppWorkflow', action: 'list', limit: 500 })
      .then((res) => {
        const list = (Array.isArray(res) ? res : res?.data) || [];
        setWorkflowsById(new Map(list.map((w) => [w.id, w.name || w.key])));
      })
      .catch(() => {});
  }, []);
  return workflowsById;
}

export default function ApiTokensPage() {
  const { fetchApi } = useAppApi();
  const nav = useNavigate();
  const [tokens, setTokens] = useState([]);
  const [apps, setApps] = useState([]);
  const workflowsById = useWorkflowsMap();
  const [applicationId, setApplicationId] = useState('');
  const [showIssue, setShowIssue] = useState(false);
  const [revealed, setRevealed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = () => {
    fetchApi('crud', {
      model: 'ApiToken', action: 'list', limit: 100, orderBy: { column: 'id', direction: 'desc' },
    })
      .then((res) => setTokens((Array.isArray(res) ? res : res?.data) || []))
      .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message))
      .finally(() => setLoading(false));
  };
  const multi = isMultiTenant();
  useEffect(() => {
    load();
    if (multi) return;
    fetchApi('applicationsList')
      .then((res) => setApps((Array.isArray(res) ? res : res?.data) || []))
      .catch(() => setApps([]));
  }, []);

  const act = async (row, action) => {
    setErr('');
    try {
      const res = await fetchApi('crud', { model: 'ApiToken', action, data: { id: row.id } });
      if (res.data && res.data.token) setRevealed(res.data.token);
      load();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    }
  };

  const appOptions = useMemo(() => [
    { label: 'All applications', value: '' },
    ...apps.map((a) => ({ label: a.name, value: String(a.id) })),
  ], [apps]);

  const rows = useMemo(() => {
    let filtered = tokens;
    if (applicationId) filtered = filtered.filter((item) => item.application_id == null || item.application_id === Number(applicationId));
    return filtered;
  }, [tokens, applicationId]);

  const columns = [
    { key: 'name', label: 'Name', render: (row) => <button onClick={() => nav(`/api-tokens/${row.id}`)} className="font-medium text-primary-600 hover:underline text-left">{row.name}</button> },
    { key: 'token_prefix', label: 'Prefix', render: (row) => <span className="font-mono text-xs">{row.token_prefix}…</span> },
    {
      key: 'workflow_id',
      label: 'Workflow',
      searchable: false,
      render: (row) => (row.workflow_id != null
        ? <span className="text-xs">{workflowsById.get(row.workflow_id) || `#${row.workflow_id}`}</span>
        : <span className="text-secondary-500 text-xs">—</span>),
    },
    ...(multi ? [{
      key: 'branch_id',
      label: 'Branch',
      searchable: false,
      render: (row) => <span className="text-xs">{branchLabel(getAuth() || {}, row.branch_id)}</span>,
    }] : []),
    {
      key: 'scopes',
      label: 'Scopes',
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {parseScopes(row.scopes).map((s) => (
            <span key={s} className="px-1.5 py-0.5 rounded bg-[var(--app-border)]/40 text-xs font-mono">{s}</span>
          ))}
        </div>
      ),
    },
    {
      key: 'rate_limit_per_minute',
      label: 'Rate limit',
      searchable: false,
      // The list read has no rate_limit_default (afterShow adds it), so "default" is
      // labelled without a number here — the detail screen shows the real figure.
      render: (row) => {
        if (row.rate_limit_per_minute === 0) return <Badge variant="warning" size="sm">unlimited</Badge>;
        if (row.rate_limit_per_minute == null) return <span className="text-secondary-500 text-xs">default</span>;
        return <span className="text-xs font-mono">{row.rate_limit_per_minute}/min</span>;
      },
    },
    { key: 'last_used_at', label: 'Last used', searchable: false, render: (row) => <span className="text-secondary-500 text-xs">{row.last_used_at ? new Date(row.last_used_at).toLocaleString() : 'never'}</span> },
    { key: 'expires_at', label: 'Expires', searchable: false, render: (row) => <span className="text-secondary-500 text-xs">{row.expires_at ? new Date(row.expires_at).toLocaleDateString() : '—'}</span> },
    { key: 'revoked_at', label: 'Status', searchable: false, render: (row) => <TokenStatusBadge row={row} /> },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      searchable: false,
      render: (row) => (
        <Can module="core" action="manage_tokens">
          <div className="flex gap-2 text-xs">
            <button className="text-primary-600 hover:underline" onClick={() => act(row, 'rotate')}>Rotate</button>
            {!row.revoked_at && <button className="text-red-600 hover:underline" onClick={() => act(row, 'revoke')}>Revoke</button>}
          </div>
        </Can>
      ),
    },
  ];

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title="API Tokens"
        subtitle={<>Authenticate external systems and AI agents against the public REST API (/api/v1) and MCP server (/mcp). See docs/PUBLIC-API.md.</>}
        actions={(
          <Can module="core" action="manage_tokens">
            <Button size="sm" onClick={() => setShowIssue((v) => !v)}>Issue token</Button>
          </Can>
        )}
      />

      {err && <Alert variant="error" title="Error">{err}</Alert>}
      {revealed && <TokenReveal token={revealed} onDismiss={() => setRevealed(null)} />}
      {showIssue && (
        <IssueDialog
          onClose={() => setShowIssue(false)}
          onIssued={(token) => {
            setShowIssue(false);
            if (token) setRevealed(token);
            load();
          }}
        />
      )}

      <DataTable
        data={rows}
        columns={columns}
        loading={loading}
        emptyText="No API tokens issued yet."
        search={{ placeholder: 'Search tokens…', keys: ['name', 'token_prefix', 'scopes'] }}
        toolbar={multi ? null : (
          <div className="w-48 sm:w-56"><Select options={appOptions} value={applicationId} onChange={setApplicationId} placeholder="All applications" searchable /></div>
        )}
        onReload={load}
      />
    </PageShell>
  );
}
